"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/app/lib/utils";
import {
   Button,
   Popover,
   PopoverContent,
   PopoverTrigger,
   Command,
   CommandEmpty,
   CommandGroup,
   CommandInput,
   CommandItem,
   CommandList
} from "@/app/components/ui";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { api_url } from "@/app/api";

export interface Model {
   id: string;
   object: string;
   created: number;
   owned_by: string;
}

interface SelectModelsProps {
   models?: Model[];
   selectedModelId?: string | undefined;
   onSelectModel?: (modelId: string | undefined) => void;
   placeholder?: string;
   searchPlaceholder?: string;
   noResultsText?: string;
   className?: string;
   popoverContentClassName?: string;
   disabled?: boolean;
}

export function SelectModels({
   models: propModels,
   selectedModelId: propSelectedModelId,
   onSelectModel,
   placeholder = "Select model...",
   searchPlaceholder = "Search model...",
   noResultsText = "No model found.",
   className,
   popoverContentClassName,
   disabled = false
}: SelectModelsProps) {
   const [open, setOpen] = React.useState(false);
   const [searchTerm, setSearchTerm] = React.useState("");
   const [models, setModels] = React.useState<Model[]>(propModels || []);
   const [isLoading, setIsLoading] = React.useState(!propModels);
   const [error, setError] = React.useState<string | null>(null);
   const [selectedModelIdInternal, setSelectedModelIdInternal] = React.useState<
      string | undefined
   >(propSelectedModelId);

   React.useEffect(() => {
      if (propSelectedModelId !== undefined) {
         setSelectedModelIdInternal(propSelectedModelId);
      }
   }, [propSelectedModelId]);
   const handleSelectModel = (modelId: string | undefined) => {
      if (onSelectModel) {
         onSelectModel(modelId);
      } else {
         setSelectedModelIdInternal(modelId);
      }
   };
   React.useEffect(() => {
      if (propModels) {
         setModels(propModels);
         return;
      }

      const fetchModels = async () => {
         try {
            setIsLoading(true);
            setError(null);
            const response = await fetchWithAuth(api_url.model_configs);
            if (!response.ok) {
               throw new Error(`Failed to fetch models: ${response.statusText}`);
            }

            const result = await response.json();
            if (result.data) {
               setModels(result.data);
            } else if (result.error) {
               throw new Error(result.error);
            }
         } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to fetch models");
            console.error("Error fetching models:", err);
         } finally {
            setIsLoading(false);
         }
      };

      fetchModels();
   }, [propModels]);

   const selectedModelId =
      propSelectedModelId !== undefined ? propSelectedModelId : selectedModelIdInternal;
   const selectedModel = models.find((model) => model.id === selectedModelId);

   const filteredModels = searchTerm
      ? models.filter((model) =>
           model.id.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : models;

   return (
      <Popover open={open} onOpenChange={setOpen}>
         <PopoverTrigger asChild>
            <Button
               variant="outline"
               role="combobox"
               aria-expanded={open}
               className={cn("max-w-[350px] min-w-[250px] justify-between", className)}
               disabled={disabled}
            >
               {selectedModel ? selectedModel.id : placeholder}
               <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
         </PopoverTrigger>
         <PopoverContent
            className={cn(
               "w-[--radix-popover-trigger-width] p-0",
               popoverContentClassName
            )}
            align="start"
         >
            <Command>
               <CommandInput
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onValueChange={setSearchTerm}
               />
               <CommandList>
                  {isLoading ? (
                     <div className="py-6 text-center text-sm">Loading models...</div>
                  ) : error ? (
                     <div className="py-6 text-center text-sm text-red-500">{error}</div>
                  ) : (
                     <>
                        <CommandEmpty>{noResultsText}</CommandEmpty>
                        <CommandGroup>
                           {filteredModels.map((model) => (
                              <CommandItem
                                 key={model.id}
                                 value={model.id}
                                 onSelect={() => {
                                    handleSelectModel(
                                       model.id === selectedModelId ? undefined : model.id
                                    );
                                    setOpen(false);
                                 }}
                              >
                                 <Check
                                    className={cn(
                                       "mr-2 h-4 w-4",
                                       selectedModelId === model.id
                                          ? "opacity-100"
                                          : "opacity-0"
                                    )}
                                 />
                                 {model.id}
                              </CommandItem>
                           ))}
                        </CommandGroup>
                     </>
                  )}
               </CommandList>
            </Command>
         </PopoverContent>
      </Popover>
   );
}
